import React, { useMemo } from 'react';
import { Row, Col } from 'react-bootstrap';
import { HEAD_DOCTOR_COPY } from '../headDoctorCopy';
import { useClinicalReports } from '../../clinical-reports/ClinicalReportsContext';
import {
  CONSULTATION_STATUS_LABELS,
  PRACTITIONER_ROLE_LABELS,
  SERVICE_LINE_LABELS,
  formatMonthKey,
  formatPractitionerLabel
} from './reportUtils';
import ReportSectionHead from './ReportSectionHead';
import StatisticalDataTable, { statPercentColumn, statRankColumn } from './StatisticalDataTable';
import { HeadDoctorStatCard } from '../ui';
import {
  ClinicalBarChart,
  ClinicalDoughnutChart,
  ClinicalLineChart,
  ReportChartCard,
  ReportsChartCol,
  ReportsChartGrid
} from './charts/ClinicalCharts';
import { CHART_PALETTE, CONSULTATION_STATUS_COLORS, SERVICE_LINE_COLORS } from './charts/chartTheme';
import {
  computeShares,
  doughnutChartData,
  labeledPieData,
  lineChartData
} from './charts/chartDataHelpers';

const COPY = HEAD_DOCTOR_COPY.REPORTS;

function statusCount(byStatus, status) {
  return Number(byStatus.find((r) => r.status === status)?.count || 0);
}

function lineCount(byLine, serviceLine) {
  return Number(byLine.find((r) => r.service_line === serviceLine)?.count || 0);
}

export default function HeadDoctorReportsConsultationsPage() {
  const { data, summary, loading } = useClinicalReports();
  const byStatus = data?.consultations?.by_status || [];
  const byLine = data?.consultations?.by_service_line || [];
  const byMonth = data?.consultations?.by_month || [];
  const topDoctors = useMemo(
    () => computeShares(data?.consultations?.top_doctors || [], 'consultation_count'),
    [data]
  );

  const pendingCount = statusCount(byStatus, 'PENDING');
  const completedCount = statusCount(byStatus, 'COMPLETED');
  const cancelledCount = statusCount(byStatus, 'CANCELLED');
  const medicalCount = lineCount(byLine, 'MEDICAL');
  const workAccidents = Number(summary.work_accidents || 0);

  const statusPie = useMemo(
    () =>
      labeledPieData(byStatus, {
        labelKey: 'status',
        labelMap: CONSULTATION_STATUS_LABELS,
        colorMap: CONSULTATION_STATUS_COLORS
      }),
    [byStatus]
  );

  const linePie = useMemo(
    () =>
      labeledPieData(byLine, {
        labelKey: 'service_line',
        labelMap: SERVICE_LINE_LABELS,
        colorMap: SERVICE_LINE_COLORS
      }),
    [byLine]
  );

  const trendData = useMemo(
    () =>
      lineChartData(
        byMonth.map((row) => formatMonthKey(row.month_key)),
        [
          {
            label: COPY.CHART_CONSULTATIONS,
            data: byMonth.map((row) => Number(row.count)),
            color: CHART_PALETTE[0],
            fill: true
          }
        ]
      ),
    [byMonth]
  );

  const doctorsBarData = useMemo(
    () => ({
      labels: topDoctors.map((r) => formatPractitionerLabel(r)),
      datasets: [
        {
          label: COPY.CHART_CONSULTATIONS,
          data: topDoctors.map((r) => Number(r.consultation_count)),
          backgroundColor: CHART_PALETTE[0],
          borderRadius: 6,
          maxBarThickness: 40
        }
      ]
    }),
    [topDoctors]
  );

  const tableColumns = [
    { key: 'rank', label: '#', className: 'text-muted', render: (_r, i) => i + 1 },
    {
      key: 'doctor_name',
      label: COPY.COL_PRACTITIONER,
      className: 'fw-semibold',
      render: (r) => r.doctor_name
    },
    {
      key: 'practitioner_role',
      label: COPY.COL_PRACTITIONER_ROLE,
      className: 'text-muted',
      render: (r) => PRACTITIONER_ROLE_LABELS[r.practitioner_role] || r.practitioner_role || '—'
    },
    statRankColumn(COPY.COL_CONSULTATIONS, 'consultation_count'),
    statPercentColumn(COPY.COL_SHARE)
  ];

  const empty = !loading && Number(summary.consultations || 0) === 0;

  return (
    <>
      <ReportSectionHead
        badge={COPY.SUB_CONSULTATIONS}
        title={COPY.PAGE_CONSULTATIONS_TITLE}
        description={COPY.PAGE_CONSULTATIONS_DESC}
      />

      <Row className="g-3 mb-4">
        <Col sm={6} lg={4} xl={2}>
          <HeadDoctorStatCard
            title={COPY.KPI_CONSULTATIONS}
            value={summary.consultations}
            icon="stethoscope"
            tone="indigo"
            loading={loading}
          />
        </Col>
        <Col sm={6} lg={4} xl={2}>
          <HeadDoctorStatCard
            title={COPY.KPI_CONSULT_MEDICAL}
            value={medicalCount}
            icon="user-md"
            tone="teal"
            loading={loading}
          />
        </Col>
        <Col sm={6} lg={4} xl={2}>
          <HeadDoctorStatCard
            title={COPY.KPI_CONSULT_PENDING}
            value={pendingCount}
            icon="alertCircle"
            tone="amber"
            loading={loading}
          />
        </Col>
        <Col sm={6} lg={4} xl={2}>
          <HeadDoctorStatCard
            title={COPY.KPI_CONSULT_DONE}
            value={completedCount}
            icon="check"
            tone="teal"
            loading={loading}
          />
        </Col>
        <Col sm={6} lg={4} xl={2}>
          <HeadDoctorStatCard
            title={COPY.KPI_CONSULT_CANCELLED}
            value={cancelledCount}
            icon="times"
            tone="slate"
            loading={loading}
          />
        </Col>
        <Col sm={6} lg={4} xl={2}>
          <HeadDoctorStatCard
            title={COPY.KPI_WORK_ACCIDENTS}
            value={workAccidents}
            icon="alertCircle"
            tone="amber"
            loading={loading}
          />
        </Col>
      </Row>

      {loading ? (
        <div className="hd-reports-panel mb-4">
          <div className="hd-reports-loading">
            <span className="spinner-border spinner-border-sm" />
            <span>{COPY.LOADING}</span>
          </div>
        </div>
      ) : empty ? (
        <div className="hd-reports-panel mb-4">
          <p className="hd-reports-empty mb-0">{COPY.EMPTY_CONSULTATIONS}</p>
        </div>
      ) : (
        <>
          <ReportsChartGrid>
            <ReportsChartCol lg={4}>
              <ReportChartCard
                title={COPY.CHART_CONSULT_STATUS_TITLE}
                subtitle={COPY.CHART_CONSULT_STATUS_DESC}
                height={280}
              >
                <ClinicalDoughnutChart
                  data={doughnutChartData(statusPie.labels, statusPie.values, statusPie.colors)}
                />
              </ReportChartCard>
            </ReportsChartCol>
            <ReportsChartCol lg={4}>
              <ReportChartCard
                title={COPY.CHART_CONSULT_LINE_TITLE}
                subtitle={COPY.CHART_CONSULT_LINE_DESC}
                height={280}
              >
                <ClinicalDoughnutChart
                  data={doughnutChartData(linePie.labels, linePie.values, linePie.colors)}
                />
              </ReportChartCard>
            </ReportsChartCol>
            <ReportsChartCol lg={4}>
              <ReportChartCard
                title={COPY.CHART_CONSULT_TREND_TITLE}
                subtitle={COPY.CHART_CONSULT_TREND_DESC}
                height={280}
              >
                <ClinicalLineChart
                  data={trendData}
                  xLabel={COPY.CHART_AXIS_PERIOD}
                  yLabel={COPY.CHART_AXIS_CONSULTATIONS}
                />
              </ReportChartCard>
            </ReportsChartCol>
          </ReportsChartGrid>

          {topDoctors.length > 0 ? (
            <ReportsChartGrid>
              <ReportsChartCol lg={12}>
                <ReportChartCard
                  title={COPY.CHART_CONSULT_DOCTORS_TITLE}
                  subtitle={COPY.CHART_CONSULT_DOCTORS_DESC}
                  height={280}
                >
                  <ClinicalBarChart
                    data={doctorsBarData}
                    yLabel={COPY.CHART_AXIS_CONSULTATIONS}
                    xLabel={COPY.COL_PRACTITIONER}
                  />
                </ReportChartCard>
              </ReportsChartCol>
            </ReportsChartGrid>
          ) : null}
        </>
      )}

      {topDoctors.length > 0 ? (
        <>
          <h3 className="hd-reports-table-title">{COPY.TABLE_DOCTORS_CONSULT_TITLE}</h3>
          <StatisticalDataTable
            columns={tableColumns}
            rows={topDoctors}
            rowKey="doctor_id"
            maxValueKey="consultation_count"
            loading={loading}
            emptyLabel={COPY.EMPTY_CONSULTATIONS}
            loadingLabel={COPY.LOADING}
          />
        </>
      ) : null}
    </>
  );
}
